/**
 * Vehicle-to-Route Assignment Generator
 * 
 * Generates vehicle assignments showing which vehicles serve which routes
 * at what times, for both dedicated and shared vehicle modes.
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { VehicleAssignment, RouteAssignmentTime } from './fleetTypes';
import { calculateRouteLoopDuration } from './vehicleCalculator';
import { addMinutesToTime, compareTimes } from './timetableUtils';

/**
 * Convert HH:mm time string to total minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to HH:mm time string
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Calculate route assignment times for a vehicle
 * Determines which loops/time periods a vehicle serves for a given route
 * 
 * @param route - The fixed route
 * @param vehicleIndex - Index of the vehicle (0-based)
 * @param totalVehicles - Total number of vehicles serving this route
 * @param trafficMultiplier - Traffic multiplier for calculations
 * @returns Start and end times for this vehicle's assignment
 */
export function calculateRouteAssignmentTimes(
  route: FixedRoute,
  vehicleIndex: number,
  totalVehicles: number,
  trafficMultiplier: number
): { startTime: string; endTime: string; loopNumbers?: number[] } {
  const routeStart = timeToMinutes(route.startTime);
  let routeEnd = route.endTime ? timeToMinutes(route.endTime) : 22 * 60;
  
  // Handle overnight shifts (e.g., 22:00 to 06:00)
  // If end time is before start time, add 24 hours to end time for calculation
  if (routeEnd < routeStart) {
    routeEnd = routeEnd + (24 * 60);
  }
  
  let operatingDuration = routeEnd - routeStart;
  
  // If route.endTime was undefined, use the normalized routeEnd for calculations
  // but still need to handle the actual end time display
  const actualRouteEnd = route.endTime ? timeToMinutes(route.endTime) : 22 * 60;
  const normalizedActualRouteEnd = actualRouteEnd < routeStart ? actualRouteEnd + (24 * 60) : actualRouteEnd;

  if (route.isCirculator) {
    // For circulators: vehicle runs CONTINUOUSLY from start time to end time
    // It completes as many loops as fit in that period
    const loopDuration = calculateRouteLoopDuration(route, trafficMultiplier);
    
    // For circulators, the vehicle ALWAYS runs from route.startTime to route.endTime
    // regardless of how many loops it can complete
    const vehicleStartTime = route.startTime;
    const vehicleEndTime = route.endTime || '22:00';
    
    if (loopDuration <= 0 || operatingDuration <= 0) {
      // Invalid configuration - vehicle still runs for full period
      return {
        startTime: vehicleStartTime,
        endTime: vehicleEndTime,
        loopNumbers: [1], // At least one loop
      };
    }
    
    // Calculate total number of loops that can fit in the operating period
    const totalLoops = Math.floor(operatingDuration / loopDuration);
    
    // Calculate which loops this vehicle handles (for multi-vehicle scenarios)
    // For single vehicle, it handles all loops
    let loopNumbers: number[] = [];
    
    if (totalLoops > 0) {
      // Distribute loops evenly across vehicles
      const loopsStart = Math.floor((vehicleIndex * totalLoops) / totalVehicles);
      const loopsEnd = Math.floor(((vehicleIndex + 1) * totalLoops) / totalVehicles);
      
      for (let i = loopsStart; i < loopsEnd; i++) {
        loopNumbers.push(i + 1); // 1-indexed
      }
    }
    
    // If no loops calculated or vehicle handles no loops, ensure at least one loop
    if (loopNumbers.length === 0) {
      loopNumbers = [1];
    }
    
    // For circulators: vehicle starts at route.startTime and runs until route.endTime
    // (Multiple vehicles can stagger their starts for better headway, but for Phase 1A,
    // we keep it simple: all vehicles start at route.startTime)
    return {
      startTime: vehicleStartTime,
      endTime: vehicleEndTime,
      loopNumbers,
    };
  } else {
    // For non-circulators: one vehicle handles the entire route
    const routeDuration = calculateRouteLoopDuration(route, trafficMultiplier);
    
    return {
      startTime: route.startTime,
      endTime: addMinutesToTime(route.startTime, routeDuration),
    };
  }
}

/**
 * Assign vehicles to routes in dedicated mode
 * Each route has its own dedicated vehicles
 * 
 * @param routes - Array of fixed routes
 * @param vehiclesPerRoute - Map of route ID to number of vehicles needed
 * @param trafficMultiplier - Traffic multiplier for calculations
 * @returns Array of vehicle assignments
 */
function assignDedicatedVehicles(
  routes: FixedRoute[],
  vehiclesPerRoute: Map<string, number>,
  trafficMultiplier: number
): VehicleAssignment[] {
  const assignments: VehicleAssignment[] = [];
  let vehicleCounter = 1;

  routes.forEach((route) => {
    const vehiclesForRoute = vehiclesPerRoute.get(route.id) || 1;

    for (let i = 0; i < vehiclesForRoute; i++) {
      const vehicleId = `vehicle-${vehicleCounter}`;
      const vehicleName = vehiclesForRoute > 1 
        ? `${route.name} - Vehicle ${i + 1}`
        : `${route.name} - Vehicle 1`;

      const assignmentTimes = calculateRouteAssignmentTimes(
        route,
        i,
        vehiclesForRoute,
        trafficMultiplier
      );

      // Get first and last stop IDs
      const startStopId = route.stops[0]?.stopId;
      const endStopId = route.isCirculator 
        ? route.stops[0]?.stopId // Circulators end where they start
        : route.stops[route.stops.length - 1]?.stopId;

      const routeAssignment: RouteAssignmentTime = {
        route,
        startTime: assignmentTimes.startTime,
        endTime: assignmentTimes.endTime,
        loopNumbers: assignmentTimes.loopNumbers,
        startStopId,
        endStopId,
      };

      // Determine overall operating period for this vehicle
      const routeStart = timeToMinutes(route.startTime);
      const routeEnd = route.endTime ? timeToMinutes(route.endTime) : 22 * 60;

      assignments.push({
        vehicleId,
        vehicleName,
        routeAssignments: [routeAssignment],
        operatingPeriod: {
          startTime: route.startTime,
          endTime: route.endTime || '22:00',
        },
      });

      vehicleCounter++;
    }
  });

  return assignments;
}

/**
 * Assign vehicles to routes in shared mode
 * Vehicles can serve multiple routes when schedules allow
 * 
 * @param routes - Array of fixed routes
 * @param totalVehicles - Total number of vehicles available
 * @param trafficMultiplier - Traffic multiplier for calculations
 * @returns Array of vehicle assignments
 */
function assignSharedVehicles(
  routes: FixedRoute[],
  totalVehicles: number,
  trafficMultiplier: number
): VehicleAssignment[] {
  const assignments: VehicleAssignment[] = [];

  // Sort routes by start time to optimize assignment order
  const sortedRoutes = [...routes].sort((a, b) => 
    compareTimes(a.startTime, b.startTime)
  );

  // For Phase 1A, use a simple allocation:
  // Each vehicle gets assigned to routes sequentially
  // More sophisticated optimization can be added in future phases
  
  const routeAssignmentsByVehicle: RouteAssignmentTime[][] = Array(totalVehicles)
    .fill(null)
    .map(() => []);

  sortedRoutes.forEach((route) => {
    const routeStart = timeToMinutes(route.startTime);
    const routeEnd = route.endTime ? timeToMinutes(route.endTime) : 22 * 60;
    const routeDuration = calculateRouteLoopDuration(route, trafficMultiplier);
    
    // For circulators, calculate loop numbers and use full operating period
    let routeEndTime: string;
    let loopNumbers: number[] | undefined;
    
    if (route.isCirculator) {
      // Circulators run from start to end time continuously
      routeEndTime = route.endTime || '22:00';
      
      // Calculate loop numbers
      const operatingDuration = routeEnd - routeStart;
      if (operatingDuration > 0 && routeDuration > 0) {
        const totalLoops = Math.floor(operatingDuration / routeDuration);
        loopNumbers = [];
        for (let i = 0; i < totalLoops; i++) {
          loopNumbers.push(i + 1); // 1-indexed
        }
      }
      if (!loopNumbers || loopNumbers.length === 0) {
        loopNumbers = [1];
      }
    } else {
      // Non-circulators run for one loop duration
      routeEndTime = addMinutesToTime(route.startTime, routeDuration);
    }
    
    // Find a vehicle that can serve this route (has time available)
    let assigned = false;
    
    for (let vehicleIndex = 0; vehicleIndex < totalVehicles; vehicleIndex++) {
      const existingAssignments = routeAssignmentsByVehicle[vehicleIndex];
      const lastAssignment = existingAssignments[existingAssignments.length - 1];
      
      // Check if vehicle can take this route
      if (!lastAssignment) {
        // Vehicle has no assignments yet
        const startStopId = route.stops[0]?.stopId;
        const endStopId = route.isCirculator 
          ? route.stops[0]?.stopId
          : route.stops[route.stops.length - 1]?.stopId;
        
        const routeAssignment: RouteAssignmentTime = {
          route,
          startTime: route.startTime,
          endTime: routeEndTime,
          loopNumbers,
          startStopId,
          endStopId,
        };
        
        routeAssignmentsByVehicle[vehicleIndex].push(routeAssignment);
        assigned = true;
        break;
      } else {
        // Check if route can be scheduled after last assignment
        const lastEnd = timeToMinutes(lastAssignment.endTime);
        const routeStartMinutes = timeToMinutes(route.startTime);
        
        // Vehicle can serve this route if it starts after the last assignment ends
        // (or overlaps, but for Phase 1A we'll keep it simple)
        if (routeStartMinutes >= lastEnd || lastEnd < routeStartMinutes) {
          const startStopId = route.stops[0]?.stopId;
          const endStopId = route.isCirculator 
            ? route.stops[0]?.stopId
            : route.stops[route.stops.length - 1]?.stopId;
          
          const routeAssignment: RouteAssignmentTime = {
            route,
            startTime: route.startTime,
            endTime: routeEndTime,
            loopNumbers,
            startStopId,
            endStopId,
          };
          
          routeAssignmentsByVehicle[vehicleIndex].push(routeAssignment);
          assigned = true;
          break;
        }
      }
    }
    
    // If not assigned, use first available vehicle
    if (!assigned) {
      const vehicleIndex = 0;
      const startStopId = route.stops[0]?.stopId;
      const endStopId = route.isCirculator 
        ? route.stops[0]?.stopId
        : route.stops[route.stops.length - 1]?.stopId;
      
      const routeAssignment: RouteAssignmentTime = {
        route,
        startTime: route.startTime,
        endTime: routeEndTime,
        loopNumbers,
        startStopId,
        endStopId,
      };
      
      routeAssignmentsByVehicle[vehicleIndex].push(routeAssignment);
    }
  });

  // Convert to VehicleAssignment format
  routeAssignmentsByVehicle.forEach((assignmentsForVehicle, vehicleIndex) => {
    if (assignmentsForVehicle.length === 0) return;
    
    const vehicleId = `vehicle-${vehicleIndex + 1}`;
    let vehicleName = `Vehicle ${vehicleIndex + 1}`;
    
    // Determine overall operating period
    const allStartTimes = assignmentsForVehicle.map(a => timeToMinutes(a.startTime));
    const allEndTimes = assignmentsForVehicle.map(a => timeToMinutes(a.endTime));
    const overallStart = minutesToTime(Math.min(...allStartTimes));
    const overallEnd = minutesToTime(Math.max(...allEndTimes));
    
    assignments.push({
      vehicleId,
      vehicleName,
      routeAssignments: assignmentsForVehicle,
      operatingPeriod: {
        startTime: overallStart,
        endTime: overallEnd,
      },
    });
  });

  return assignments;
}

/**
 * Generate vehicle assignments for routes
 * 
 * @param routes - Array of fixed routes
 * @param vehicles - Total number of vehicles available
 * @param sharingMode - 'dedicated' or 'shared' mode
 * @param trafficMultiplier - Traffic multiplier for calculations
 * @returns Array of vehicle assignments
 */
export function generateVehicleAssignments(
  routes: FixedRoute[],
  vehicles: number,
  sharingMode: 'dedicated' | 'shared',
  trafficMultiplier: number
): VehicleAssignment[] {
  if (routes.length === 0) {
    return [];
  }

  if (sharingMode === 'dedicated') {
    // Calculate vehicles per route
    const vehiclesPerRoute = new Map<string, number>();
    let vehicleCount = 0;
    
    routes.forEach((route) => {
      // For dedicated mode, we need to calculate how many vehicles each route needs
      // This should have been pre-calculated, but we'll do a simple allocation here
      // In practice, the vehicle count passed in should already account for this
      const routeVehicles = Math.max(1, Math.ceil(vehicles / routes.length));
      vehiclesPerRoute.set(route.id, routeVehicles);
      vehicleCount += routeVehicles;
    });
    
    return assignDedicatedVehicles(routes, vehiclesPerRoute, trafficMultiplier);
  } else {
    // Shared mode: all vehicles can be shared
    return assignSharedVehicles(routes, vehicles, trafficMultiplier);
  }
}

/**
 * Generate timetable entries from vehicle assignments
 * Creates a simplified view for display purposes
 * 
 * @param assignments - Array of vehicle assignments
 * @returns Array of timetable entry objects
 */
export interface TimetableEntry {
  vehicleId: string;
  vehicleName: string;
  routeId: string;
  routeName: string;
  startTime: string;
  endTime: string;
  loopNumbers?: number[];
}

export function generateTimetableEntries(
  assignments: VehicleAssignment[]
): TimetableEntry[] {
  const entries: TimetableEntry[] = [];

  assignments.forEach((assignment) => {
    assignment.routeAssignments.forEach((routeAssignment) => {
      entries.push({
        vehicleId: assignment.vehicleId,
        vehicleName: assignment.vehicleName,
        routeId: routeAssignment.route.id,
        routeName: routeAssignment.route.name,
        startTime: routeAssignment.startTime,
        endTime: routeAssignment.endTime,
        loopNumbers: routeAssignment.loopNumbers,
      });
    });
  });

  // Sort by vehicle ID, then by start time
  entries.sort((a, b) => {
    if (a.vehicleId !== b.vehicleId) {
      return a.vehicleId.localeCompare(b.vehicleId);
    }
    return compareTimes(a.startTime, b.startTime);
  });

  return entries;
}

